import React from "react";
import Container from "@mui/material/Container";
import { useNavigate } from "react-router-dom";

export default function ChildSafetyStandardsPage() {
  const navigate = useNavigate();

  const sections = [
    {
      id: "introduction",
      title: "Child Safety Standards – Koin App",
      content:
        "Koin App is committed to ensuring a safe and secure environment for all users. We have a zero-tolerance policy for child sexual abuse and exploitation (CSAE) and any related content. Child safety is not just a policy for us – it is a fundamental principle that guides every decision we make, from product development to community management. We understand the profound responsibility we bear in protecting vulnerable users and preventing harm. Our commitment extends beyond legal compliance to encompassing ethical practices and proactive measures designed to prevent abuse before it occurs.",
    },
    {
      id: "commitments",
      title: "Our Commitments",
      content: "We are dedicated to maintaining the highest standards of child protection across our platform.",
      details: [
        "We strictly prohibit any form of child sexual abuse material (CSAM) on our platform. This includes any content that sexualizes, exploits, or endangers children in any form, whether real, simulated, or illustrated",
        "Users are not allowed to upload, share, or distribute harmful or illegal content. This encompasses content that promotes violence, grooming behavior, exploitation, or any form of abuse targeting minors or vulnerable individuals",
        "Any violation of these policies will result in immediate action, including account suspension or removal. We maintain permanent records of violations and cooperate fully with law enforcement authorities",
        "We invest continuously in advanced detection technologies, including machine learning and AI-powered systems, to identify and remove prohibited content automatically",
        "Our team undergoes regular training on child safety issues, recognizing grooming tactics, and appropriate response protocols to ensure swift and effective action",
      ],
    },
    {
      id: "reporting",
      title: "Reporting",
      content:
        "Users can report inappropriate or harmful content directly within the app through our secure reporting mechanism. We review all reports promptly and take necessary action with urgency and sensitivity. We understand that users are our eyes and ears on the platform, and we value every report as an opportunity to protect our community. Our reporting process is designed to be simple, accessible, and confidential to encourage community members to speak up without fear of retaliation or privacy concerns.",
      details: [
        "Use the in-app reporting button to flag any suspicious or harmful content immediately",
        "Provide detailed information about the content to help our team investigate thoroughly and take appropriate action",
        "Reports are reviewed by our trained specialists within 24 hours, with critical reports prioritized for immediate action",
        "We maintain strict confidentiality of reporters to protect their identity and ensure they can report safely without fear",
      ],
    },
    {
      id: "compliance",
      title: "Compliance",
      content:
        "We comply with all applicable child safety laws and regulations across jurisdictions where we operate. We cooperate with law enforcement authorities when required and maintain a transparent approach to our compliance efforts. Our legal team continuously monitors regulatory changes and updates our policies to ensure alignment with evolving standards and best practices in child protection.",
      details: [
        "Regular audits of our systems and processes to ensure ongoing compliance with applicable regulations",
        "Cooperation with law enforcement agencies, child safety organizations, and regulatory bodies on investigations",
        "Maintenance of detailed records for accountability and transparency in our child safety efforts",
      ],
    },
    {
      id: "prevention-measures",
      title: "Prevention Measures",
      content:
        "Monitoring and moderation mechanisms are in place to detect and prevent abuse before harm occurs. We employ a multi-layered approach combining technological solutions with human expertise. We continuously improve our systems to ensure user safety by staying ahead of emerging threats and abuse tactics. Our prevention strategy includes proactive monitoring, community education, and partnerships with child safety organizations.",
      details: [
        "Real-time monitoring of user activities, content uploads, and communications using advanced algorithmic detection",
        "Automated content scanning systems that identify and flag potentially harmful material for human review",
        "Age-gating mechanisms and verification processes to ensure compliance with age restrictions",
        "Regular vulnerability assessments and security audits to identify and address potential exploitation vectors",
        "Continuous updates to our detection algorithms based on emerging threats and abuse patterns",
        "Partnership with industry-leading child safety organizations and research institutions to stay informed of latest threats",
      ],
    },
    {
      id: "contact",
      title: "Contact",
      content:
        "If you have concerns regarding child safety, please contact us immediately at support@koinapp.co. Our dedicated child safety team is available to address your concerns with the utmost priority and confidentiality. We take every report seriously and are committed to responding swiftly and appropriately. Whether you have witnessed suspicious activity, need to report content, or have general inquiries about our safety measures, we are here to listen and take action.",
      details: [
        "Email: support@koinapp.co for detailed reports and concerns",
        "In-app reporting feature available 24/7 for quick incident reporting",
        "Our child safety team responds to all critical reports within hours",
        "For urgent emergencies involving immediate danger to children, please contact local law enforcement",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Container
        maxWidth="md"
        sx={{
          pt: 3,
          pb: 4,
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-orange-600 hover:text-orange-700 font-medium"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 16L6 10L12 4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Child Safety Standards</h1>
          <div className="w-20" />
        </div>

        {/* Content */}
        <div className="space-y-6">
          {sections.map((section) => (
            <div key={section.id} className="bg-white rounded-lg p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <h2 className="text-lg font-bold text-gray-900 mb-3">
                {section.title}
              </h2>
              <p className="text-gray-700 leading-relaxed text-sm mb-3">
                {section.content}
              </p>
              {section.details && (
                <ul className="space-y-2 ml-4">
                  {section.details.map((detail, index) => (
                    <li
                      key={index}
                      className="text-gray-700 text-sm flex items-start gap-2"
                    >
                      <span className="text-orange-600 font-bold mt-0.5">•</span>
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="mt-8 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200 text-center">
          <p className="text-gray-700 text-sm mb-3">
            Have concerns regarding child safety?
          </p>
          <a
            href="mailto:support@koinapp.co"
            className="inline-block px-6 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors"
          >
            Contact Us
          </a>
        </div>
      </Container>
    </div>
  );
}
